import {
  Activity,
  Boxes,
  Calendar,
  DollarSign,
  FolderTree,
  Globe,
  // HelpCircle,
  Home,
  Landmark,
  Package,
  PawPrint,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  SquareKanban,
  Store,
  Tag,
  Truck,
  User,
  Users,
  Wallet,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import {
  CLINICAL_RESOURCES,
  RESOURCES,
  type ResourceId,
} from "@/context/auth/resources";

export interface SidebarItem {
  id: string;
  title: string;
  url: string;
  icon?: any;
  items?: SubItem[];
  isActive?: boolean;
  /**
   * @deprecated Gate por STRING DE RUTA (vía `paths.ts`, con pérdida). Se migra a
   * `resource` (resource id del entitlement). Ver #49.
   */
  permissionLink?: string[] | "owner" | "free";
  /**
   * Gate canónico: id(s) de recurso del entitlement. Cuando está presente, el
   * sidebar filtra con `can(user, resource, "view")` — el mismo vocab que el
   * editor y el borde. Tiene prioridad sobre `permissionLink`.
   */
  resource?: ResourceId | ResourceId[];
  /**
   * Marca la sección clínica: su LABEL lo decide `organization.humanHealth` en
   * runtime ("Clínica" true / "Veterinaria" false), no el config (#59). El `title`
   * de acá es el fallback mientras identity no expone `humanHealth`.
   */
  clinicalLabel?: boolean;
  planFeature?: string;
  /**
   * Sólo lo ve el DUEÑO del negocio. Se suma al gate de `resource` o
   * `permissionLink`, no lo reemplaza: el dueño igual tiene que pasarlo (ver
   * `withOwnerOnly`). La ruta tiene que declarar lo mismo en su `ClientLayout`,
   * o el grant del empleado la seguiría abriendo por URL.
   */
  ownerOnly?: boolean;
}

export interface SidebarItemWithItems extends SidebarItem {
  items: SubItem[];
}

interface SubItem {
  title: string;
  url: string;
  permissionLink?: string;
  /** Gate canónico por resource id (ver `SidebarItem.resource`). */
  resource?: ResourceId | ResourceId[];
  /** Sólo en el escritorio, como la caja offline (D5). */
  soloEscritorio?: boolean;
}

export const sidebarLinks: SidebarItem[] = [
  {
    id: uuidv4(),
    title: "Home",
    url: "/",
    icon: Home,
    isActive: true,
    permissionLink: "free",
  },
  {
    // Monitor del negocio (trd-client#282): las cifras del negocio, así que
    // sólo el dueño (trd-client#285). `resource` sigue mandando: un dueño sin
    // el módulo `report/monitor` tampoco lo ve, porque la ruta no lo dejaría
    // entrar.
    id: "monitor",
    title: "Monitor",
    url: "/monitor",
    icon: Activity,
    resource: RESOURCES.REPORT_MONITOR,
    ownerOnly: true,
  },
  {
    id: uuidv4(),
    title: "Chat",
    url: "/chat",
    icon: Sparkles,
    isActive: true,
    permissionLink: "free",
    planFeature: "ai/chat",
  },
  {
    id: uuidv4(),
    title: "Usuarios",
    url: "/usuarios",
    icon: User,
    resource: RESOURCES.AUTH_USERS,
  },
  {
    id: uuidv4(),
    title: "Clientes",
    url: "/clientes",
    resource: [RESOURCES.PEOPLE_BUYERS, RESOURCES.COLLECTION_INITIAL_DEBT],
    icon: Users,
    items: [
      {
        title: "Administrar Clientes",
        url: "/clientes",
        resource: RESOURCES.PEOPLE_BUYERS,
      },
      {
        title: "Deuda inicial",
        url: "/clientes/deuda-inicial",
        resource: RESOURCES.COLLECTION_INITIAL_DEBT,
      },
      {
        title: "Deudores",
        url: "/clientes/deudores",
        resource: RESOURCES.PEOPLE_BUYERS,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Veterinaria",
    url: "/clinical",
    icon: PawPrint,
    clinicalLabel: true,
    // Sección clínica migrada al vocab REFINADO (platform#20 → identity#56): gatea
    // por `clinical/patients|visits|practitioners`, NO por `veterinary/*` (murió) ni
    // `clinical` plano. Data-driven (#56): visible si el usuario puede sobre algún
    // recurso clínico. El LABEL (Clínica/Veterinaria) lo pone el sidebar por
    // `organization.humanHealth`, no el vocab. Se removió el plan-gate
    // `veterinary/patients` (vocab viejo que además escondía la sección).
    resource: CLINICAL_RESOURCES,
    items: [
      {
        title: "Resumen",
        url: "/clinical",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Calendario",
        url: "/clinical/calendar",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Sala de espera",
        url: "/clinical/waiting-room",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Pacientes",
        url: "/clinical/patients",
        resource: RESOURCES.CLINICAL_PATIENTS,
      },
      {
        title: "Visitas",
        url: "/clinical/visits",
        resource: RESOURCES.CLINICAL_VISITS,
      },
      {
        title: "Internaciones",
        url: "/clinical/stays",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Camas / Boxes",
        url: "/clinical/occupancy-resources",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Veterinarios",
        url: "/clinical/veterinarians",
        resource: RESOURCES.CLINICAL_PRACTITIONERS,
      },
      {
        title: "Obras sociales",
        url: "/clinical/insurance-plans",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Servicios",
        url: "/clinical/veterinary-services",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Protocolos vacunas/desparasitación",
        url: "/clinical/protocols",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Recordatorios",
        url: "/clinical/reminders",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Plantillas certificados",
        url: "/clinical/certificate-templates",
        resource: CLINICAL_RESOURCES,
      },
      {
        title: "Certificados emitidos",
        url: "/clinical/certificates",
        resource: CLINICAL_RESOURCES,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Proveedores",
    url: "/proveedores",
    resource: RESOURCES.SUPPLIER_SUPPLIERS,
    icon: Truck,
  },
  {
    id: uuidv4(),
    title: "Marcas",
    url: "/proveedores/marcas",
    icon: Tag,
    resource: RESOURCES.SUPPLIER_BRANDS,
  },
  {
    id: uuidv4(),
    title: "Locales",
    url: "/inventario/locales",
    icon: Store,
    resource: RESOURCES.INVENTORY_WAREHOUSES,
  },
  {
    id: uuidv4(),
    title: "Categorías",
    url: "/inventario/categorias",
    icon: FolderTree,
    resource: RESOURCES.CATALOG_CATEGORIES,
  },
  {
    id: uuidv4(),
    title: "Productos",
    url: "/inventario/productos",
    icon: Package,
    resource: RESOURCES.CATALOG_PRODUCTS,
    items: [
      {
        title: "Administrar productos",
        url: "/inventario/productos",
        resource: RESOURCES.CATALOG_PRODUCTS,
      },
      {
        title: "Carga masiva",
        url: "/inventario/productos/carga-masiva",
        resource: RESOURCES.CATALOG_PRODUCTS,
      },
      {
        // EL FIX: Sueltos es su propio recurso del entitlement, no Productos.
        // Antes gateaba sobre catalog/products → un empleado sin loose-products
        // lo veía igual. Ahora dar/quitar "Productos sueltos" surte efecto acá.
        title: "Sueltos",
        url: "/inventario/sueltos",
        resource: RESOURCES.INVENTORY_LOOSE_PRODUCTS,
      },
      {
        // Un grupo es un conjunto de variantes, así que va con Productos y
        // comparte su recurso. Ver el comentario de la ruta en routes.tsx.
        title: "Grupos",
        url: "/inventario/grupos",
        resource: RESOURCES.CATALOG_PRODUCTS,
      },
      {
        // Los grupos de CONSUMO son otra cosa: dicen cuántos días dura lo que
        // hay adentro, para programar el recordatorio de reposición al vender.
        // Mismo recurso por el mismo motivo que los de arriba.
        title: "Grupos de consumo",
        url: "/inventario/grupos-de-consumo",
        resource: RESOURCES.CATALOG_PRODUCTS,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Inventario",
    url: "/inventario",
    resource: [RESOURCES.INVENTORY_INVENTORIES, RESOURCES.INVENTORY_TRANSFER],
    icon: Boxes,
    items: [
      {
        title: "Inventario",
        url: "/inventario",
        resource: RESOURCES.INVENTORY_INVENTORIES,
      },
      {
        title: "Transferencia de mercaderías",
        url: "/inventario/transferencia-de-mercaderia",
        resource: RESOURCES.INVENTORY_TRANSFER,
      },
      {
        title: "Stocks mínimos",
        url: "/inventario/stocks-minimos",
        resource: RESOURCES.INVENTORY_INVENTORIES,
      },
      {
        title: "Valuación de inventario",
        url: "/inventario/valuacion",
        resource: RESOURCES.INVENTORY_INVENTORIES,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Precios y costos",
    url: "/precios",
    resource: [
      RESOURCES.PRICING_MODIFY_PRICE,
      RESOURCES.PRICING_PRICE_TEMPLATE,
      RESOURCES.PRICING_PRICE_BULK,
      RESOURCES.PRICING_PRICE_LIST,
      RESOURCES.PRICING_PRICE_PLAN,
      RESOURCES.PRICING_PRICE_WHOLESALER_PLAN,
      RESOURCES.PRICING_CHECKOUT_FEES,
    ],
    icon: DollarSign,
    items: [
      {
        title: "Administrar Precios",
        url: "/precios",
        resource: RESOURCES.PRICING_MODIFY_PRICE,
      },
      {
        title: "Actualizar precios",
        url: "/precios/actualizar-precios",
        resource: RESOURCES.PRICING_MODIFY_PRICE,
      },
      {
        title: "Plantillas",
        url: "/precios/plantillas",
        resource: RESOURCES.PRICING_PRICE_TEMPLATE,
      },
      {
        title: "Aumento masivo",
        url: "/precios/aumento-masivo",
        resource: RESOURCES.PRICING_PRICE_BULK,
      },
      {
        title: "Listas minoristas",
        url: "/precios/listas-minoristas",
        resource: RESOURCES.PRICING_PRICE_LIST,
      },
      {
        title: "Planes minoristas",
        url: "/precios/planes-minoristas",
        resource: RESOURCES.PRICING_PRICE_PLAN,
      },
      {
        title: "Planes mayoristas",
        url: "/precios/planes-mayoristas",
        resource: RESOURCES.PRICING_PRICE_WHOLESALER_PLAN,
      },
      {
        title: "Medios de pago y recargos",
        url: "/precios/recargos",
        resource: RESOURCES.PRICING_CHECKOUT_FEES,
      },
      {
        title: "Listas de precios",
        url: "/precios/listas-de-precios",
        resource: RESOURCES.PRICING_MODIFY_PRICE,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Compras",
    url: "/compras",
    resource: [
      RESOURCES.PURCHASE_PURCHASES,
      RESOURCES.PURCHASE_STOCK_ORDER,
      RESOURCES.DISBURSEMENT_DISBURSEMENTS,
      RESOURCES.PURCHASE_INITIAL_DEBT,
    ],
    icon: ShoppingBag,
    items: [
      {
        title: "Administrar compras",
        url: "/compras",
        resource: RESOURCES.PURCHASE_PURCHASES,
      },
      {
        title: "Cargar factura",
        url: "/compras/cargar-factura",
        // Gate canónico por resource id (#49): la ingesta CREA una compra, así que
        // gatea igual que Compras. Antes usaba `permissionLink` (string de ruta, legacy).
        resource: RESOURCES.PURCHASE_PURCHASES,
      },
      {
        title: "Vencimientos",
        url: "/compras/vencimientos",
        resource: RESOURCES.PURCHASE_PURCHASES,
      },
      {
        title: "Solicitud de compra",
        url: "/compras/solicitud-compra",
        resource: RESOURCES.PURCHASE_STOCK_ORDER,
      },
      {
        title: "Pagos",
        url: "/compras/pagos",
        resource: RESOURCES.DISBURSEMENT_DISBURSEMENTS,
      },
      {
        title: "Pagos B2B",
        url: "/compras/pagos-b2b",
        resource: RESOURCES.DISBURSEMENT_DISBURSEMENTS,
      },
      {
        title: "Deuda inicial con proveedores",
        url: "/compras/deuda-inicial",
        resource: RESOURCES.PURCHASE_INITIAL_DEBT,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Ventas",
    url: "/ventas",
    resource: RESOURCES.ORDER_ORDERS,
    icon: ShoppingCart,
    items: [
      {
        title: "Ordenes de venta",
        url: "/ventas",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        // La cola de reposición: qué se le está por acabar a cada cliente.
        // Nace de una venta, por eso vive acá y comparte su recurso.
        title: "Recordatorios",
        url: "/ventas/recordatorios",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        title: "Crear orden de venta",
        url: "/ventas/crear",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        title: "Crear venta mostrador",
        url: "/ventas/crear-rapida",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        title: "Envíos",
        url: "/ventas/envios",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        title: "Preparación de envíos",
        url: "/ventas/preparacion-de-envios",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        title: "Retiros pendientes",
        url: "/ventas/retiros-pendientes",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        // Las observaciones, los topes y los cajeros de la caja offline
        // (#346). Pide «Depósitos», como dar de alta una caja, y es sólo del
        // escritorio, como toda la caja offline (D5).
        title: "Cajas de escritorio",
        url: "/ventas/cajas-de-escritorio",
        resource: RESOURCES.INVENTORY_WAREHOUSES,
        soloEscritorio: true,
      },
      {
        title: "Facturación",
        url: "/ventas/facturacion",
        resource: RESOURCES.ORDER_ORDERS,
      },
      {
        title: "Productos de regalo",
        url: "/ventas/productos-regalo",
        resource: RESOURCES.ORDER_ORDERS,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Reservas",
    url: "/reservas",
    resource: [
      RESOURCES.RESERVATION_RESERVATIONS,
      RESOURCES.RESERVATION_RESERVABLES,
      RESOURCES.RESERVATION_SERVICES,
      RESOURCES.RESERVATION_BOOKINGS,
    ],
    icon: Calendar,
    items: [
      {
        title: "Administrar reservas",
        url: "/reservas",
        resource: RESOURCES.RESERVATION_RESERVATIONS,
      },
      {
        title: "Crear Reserva",
        url: "/reservas/crear",
      },
      {
        title: "Recursos",
        url: "/reservas/recursos",
        resource: RESOURCES.RESERVATION_RESERVABLES,
      },
      {
        title: "Servicios",
        url: "/reservas/servicios",
        resource: RESOURCES.RESERVATION_SERVICES,
      },
      {
        title: "Políticas de cancelación",
        url: "/reservas/politicas-cancelacion",
      },
      {
        title: "Booking online",
        url: "/reservas/booking",
        resource: RESOURCES.RESERVATION_BOOKINGS,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Cobranzas",
    url: "/cobros",
    resource: RESOURCES.COLLECTION_DEPOSITS,
    icon: Landmark,
    items: [
      {
        title: "Administrar cobros",
        url: "/cobros",
        resource: RESOURCES.COLLECTION_DEPOSITS,
      },
      {
        title: "Cobrar y pagar",
        url: "/cobros/cobrar-y-pagar",
        resource: RESOURCES.COLLECTION_DEPOSITS,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Tableros",
    url: "/tableros",
    icon: SquareKanban,
    resource: RESOURCES.BOARDS,
  },
  {
    id: uuidv4(),
    title: "Cupones y Ofertas",
    url: "/cupones",
    resource: [RESOURCES.COUPON_COUPONS, RESOURCES.OFFER],
    icon: Tag,
    items: [
      {
        title: "Cupones",
        url: "/cupones",
        resource: RESOURCES.COUPON_COUPONS,
      },
      {
        title: "Ofertas",
        url: "/cupones/ofertas",
        resource: RESOURCES.OFFER,
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Tesorería",
    url: "/caja",
    resource: [
      RESOURCES.TREASURY_CASHDESKS,
      RESOURCES.TREASURY_EXPENSES,
      RESOURCES.TREASURY_CHECKS,
    ],
    icon: Wallet,
    items: [
      {
        title: "Caja",
        url: "/caja",
        resource: RESOURCES.TREASURY_CASHDESKS,
      },
      {
        title: "Caja por cajero",
        url: "/caja/por-cajero",
        resource: RESOURCES.TREASURY_CASHDESKS,
      },
      {
        title: "Cajas cerradas",
        url: "/caja/cajas-cerradas",
        resource: RESOURCES.TREASURY_CASHDESKS,
      },
      {
        title: "Gastos",
        url: "/caja/gastos",
        resource: RESOURCES.TREASURY_EXPENSES,
      },
      {
        title: "Adelanto de comisiones",
        url: "/caja/adelanto-de-comisiones",
        resource: RESOURCES.TREASURY_EXPENSES,
      },
      {
        title: "Cheques",
        url: "/caja/cheques",
        resource: RESOURCES.TREASURY_CHECKS,
      },
      {
        title: "Cartera de cheques",
        url: "/caja/cartera-de-cheques",
        resource: RESOURCES.TREASURY_CHECKS,
      },
    ],
  },
  {
    id: "big-supplier-seller",
    title: "Ventas B2B",
    url: "/ventas-b2b",
    icon: Store,
    resource: RESOURCES.BIG_SUPPLIER_SELLER,
    items: [
      {
        title: "Productos publicados",
        url: "/ventas-b2b/productos",
        resource: RESOURCES.BIG_SUPPLIER_SELLER,
      },
      {
        title: "Solicitudes",
        url: "/ventas-b2b/solicitudes",
        resource: RESOURCES.BIG_SUPPLIER_SELLER,
      },
      {
        title: "Órdenes",
        url: "/ventas-b2b/ordenes",
        resource: RESOURCES.BIG_SUPPLIER_SELLER,
      },
      {
        title: "Clientes B2B",
        url: "/ventas-b2b/clientes",
        resource: RESOURCES.BIG_SUPPLIER_SELLER,
      },
      {
        title: "Pagos recibidos",
        url: "/ventas-b2b/pagos",
        resource: RESOURCES.BIG_SUPPLIER_SELLER,
      },
      {
        title: "Aumentos de precios",
        url: "/ventas-b2b/aumentos",
        resource: RESOURCES.BIG_SUPPLIER_SELLER,
      },
    ],
  },
  {
    id: "mi-tienda-online",
    title: "Mi Tienda Online",
    url: "/mi-tienda-online",
    icon: Globe,
    permissionLink: "owner",
    // El módulo de tienda online se vende como addon
    // (category='module') en trd-core. El sidebar solo lo muestra a
    // businesses que tengan este feature en businessPlanFeatures.
    planFeature: "ecommerce/storefront",
    items: [
      {
        title: "Dashboard",
        url: "/mi-tienda-online",
      },
      {
        title: "Apariencia",
        url: "/mi-tienda-online/apariencia",
      },
      {
        title: "Información",
        url: "/mi-tienda-online/informacion",
      },
      {
        title: "Envíos",
        url: "/mi-tienda-online/envios",
      },
    ],
  },
  {
    id: uuidv4(),
    title: "Configuración",
    url: "/configuracion",
    icon: Settings,
    permissionLink: "free",
  },
  // {
  //   id: uuidv4(),
  //   title: "Información y ayuda",
  //   url: "/informacion",
  //   icon: HelpCircle,
  // },
];
